import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, MessageSquare, Paperclip, Plus, Search, X } from "lucide-react";
import {
  BotoesExportacao,
  CampoPeriodo,
  ErrorAlert,
  LoadingSpinner,
} from "../../../components/ui";
import type {
  AbaChamado,
  ChamadoGestao,
  SegmentoChamado,
} from "../../../types/portalCliente";
import {
  exportarChamados,
  useCapacidades,
  useListaChamados,
  type FiltrosGestao,
} from "./api";
import { useAuthUser } from "../../../stores/authStore";
import { filtrarPorBusca } from "./busca";
import { QuadroSeveridades } from "./QuadroSeveridades";
import { usePodeAbrirChamado } from "./usePodeAbrirChamado";
import { ResumoChamados } from "./ResumoChamados";
import { descricaoUtil, resumirAba, resumirResolvidos } from "./resumo";
import {
  classesSeveridade,
  formatarData,
  rotuloSeveridade,
} from "./severidade";

const ABAS: { id: AbaChamado; rotulo: string; contador: keyof ContadorMapa }[] = [
  { id: "Todos", rotulo: "Todos", contador: "todos" },
  { id: "Novas", rotulo: "Novas solicitações", contador: "novas" },
  { id: "Andamento", rotulo: "Em andamento", contador: "andamento" },
  { id: "Finalizados", rotulo: "Finalizados", contador: "finalizados" },
  { id: "Cancelados", rotulo: "Cancelados", contador: "cancelados" },
];

type ContadorMapa = {
  todos: number;
  novas: number;
  andamento: number;
  finalizados: number;
  cancelados: number;
};

const SEGMENTOS: { id: SegmentoChamado; rotulo: string }[] = [
  { id: "Todos", rotulo: "Todos" },
  { id: "Suporte", rotulo: "Suporte" },
  { id: "Novas", rotulo: "Novas funcionalidades" },
];

// A tela abre onde o cliente age, não onde ele consulta: solicitações novas de
// suporte. "Todos" mistura anos de histórico com o que acabou de ser pedido.
const FILTROS_INICIAIS: FiltrosGestao = { aba: "Novas", segmento: "Suporte" };

/** O foco está onde a pessoa digita: aí a tecla é texto, não atalho. */
const digitandoEm = (elemento: Element | null): boolean =>
  elemento instanceof HTMLElement &&
  (elemento.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(elemento.tagName));

const ChamadosPage = () => {
  const navigate = useNavigate();
  const [filtros, setFiltros] = useState<FiltrosGestao>(FILTROS_INICIAIS);
  const [busca, setBusca] = useState("");
  const campoBusca = useRef<HTMLInputElement>(null);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    isPlaceholderData,
  } = useListaChamados(filtros);

  // O botão só existe onde a escrita está ligada: com a bandeira desligada a
  // rota de POST nem é registrada, e o clique levaria a um 404 sem sentido.
  const { data: capacidades } = useCapacidades();

  // A ordem dos chamados é decisão do cliente (a API recusa a equipe); abrir
  // chamado, a equipe só com a função no perfil do ProService.
  const usuario = useAuthUser();
  const equipe = Boolean(usuario?.equipe);

  // Os finalizados vêm em consulta própria e sem período: "resolvidos em 90
  // dias" é o que foi entregue, e não faz sentido depender de qual aba está
  // aberta nem do recorte de datas que a pessoa escolheu.
  const finalizados = useListaChamados({
    aba: "Finalizados",
    segmento: filtros.segmento,
  });

  // A gravação da ordem exige o conjunto inteiro da aba Novas, sem segmento nem
  // período: a prioridade é a posição dentro do grupo, então um lote parcial
  // embaralharia quem ficou de fora do filtro. Só é buscada quando serve.
  const podeReordenar =
    filtros.aba === "Novas" && Boolean(capacidades?.reordenacaoHabilitada) && !equipe;
  const novasCompleta = useListaChamados(
    { aba: "Novas", segmento: "Todos" },
    podeReordenar
  );

  const temPeriodo = Boolean(filtros.inicio || filtros.fim);

  const contadores = data?.contadores;
  const gerais = data?.contadoresGerais;

  const itens = useMemo(() => data?.itens ?? [], [data]);

  // A busca filtra na tela o que a aba já trouxe: nada volta ao servidor, e o
  // quadro continua com a ordem completa por baixo, como faz com o segmento.
  const buscando = busca.trim().length > 0;
  const filtrados = useMemo(() => filtrarPorBusca(itens, busca), [itens, busca]);

  const resumo = useMemo(() => resumirAba(filtrados), [filtrados]);
  const resolvidos = useMemo(
    () => (finalizados.data ? resumirResolvidos(finalizados.data.itens) : undefined),
    [finalizados.data]
  );

  // Atalhos da origem: "N" abre chamado novo e "/" vai para a busca.
  const podeAbrir = usePodeAbrirChamado();
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented) return;
      if (digitandoEm(document.activeElement)) return;

      if ((e.key === "n" || e.key === "N") && podeAbrir) {
        e.preventDefault();
        navigate("/chamados/novo");
      } else if (e.key === "/") {
        e.preventDefault();
        campoBusca.current?.focus();
      }
    };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [navigate, podeAbrir]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-800 via-blue-600 to-blue-500 bg-clip-text text-transparent">
            Chamados
          </h1>
          <p className="text-gray-600 mt-1">
            {equipe
              ? `Chamados de ${usuario?.nomeCliente}${usuario?.nomeContrato ? ` · ${usuario.nomeContrato}` : ""}, por situação.`
              : "Acompanhe as solicitações do seu cliente por situação."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <BotoesExportacao
            aoExportar={(formato) => exportarChamados(filtros, formato)}
            desabilitado={isLoading || itens.length === 0}
          />
          {podeAbrir && (
            <Link
              to="/chamados/novo"
              title="Abrir chamado (atalho: N)"
              aria-keyshortcuts="N"
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-gradient-to-r from-blue-800 to-blue-600 px-4 text-sm font-medium text-white shadow-sm transition-colors hover:from-blue-900 hover:to-blue-700"
            >
              <Plus className="h-4 w-4" />
              Abrir chamado
            </Link>
          )}
        </div>
      </div>

      {!isLoading && !isError && (
        <ResumoChamados
          resumo={resumo}
          resolvidos={resolvidos}
          carregandoResolvidos={finalizados.isLoading}
          equipe={equipe}
        />
      )}

      {/* Abas com contadores */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-2">
        <nav className="flex flex-wrap gap-1" aria-label="Situação do chamado">
          {ABAS.map((aba) => {
            const ativa = filtros.aba === aba.id;
            const total = contadores?.[aba.contador];
            return (
              <button
                key={aba.id}
                type="button"
                onClick={() => setFiltros((f) => ({ ...f, aba: aba.id }))}
                aria-current={ativa ? "page" : undefined}
                className={[
                  "px-4 py-2 rounded-xl text-sm font-medium transition-colors",
                  ativa
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-gray-600 hover:bg-blue-50 hover:text-blue-700",
                ].join(" ")}
              >
                {aba.rotulo}
                {total !== undefined && (
                  <span
                    className={[
                      "ml-2 rounded-full px-2 py-0.5 text-xs font-semibold",
                      ativa ? "bg-white/20" : "bg-gray-100 text-gray-700",
                    ].join(" ")}
                  >
                    {total}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Busca, segmento e período */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-4 flex flex-wrap items-end gap-6">
        <div className="min-w-[16rem] flex-1">
          <label
            htmlFor="busca-chamados"
            className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
          >
            Buscar
          </label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              ref={campoBusca}
              id="busca-chamados"
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape" && busca) {
                  e.preventDefault();
                  setBusca("");
                }
              }}
              placeholder="Assunto, descrição, produto ou #número"
              aria-keyshortcuts="/"
              autoComplete="off"
              className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-9 text-sm text-gray-800 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 [&::-webkit-search-cancel-button]:hidden"
            />
            {busca ? (
              <button
                type="button"
                onClick={() => {
                  setBusca("");
                  campoBusca.current?.focus();
                }}
                aria-label="Limpar busca"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 transition-colors hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            ) : (
              <kbd className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 rounded border border-gray-200 bg-gray-50 px-1.5 font-sans text-[11px] font-medium text-gray-500">
                /
              </kbd>
            )}
          </div>
        </div>

        <div>
          <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Tipo de demanda
          </span>
          <div className="flex gap-1">
            {SEGMENTOS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setFiltros((f) => ({ ...f, segmento: s.id }))}
                className={[
                  "px-3 py-1.5 rounded-lg text-sm border transition-colors",
                  filtros.segmento === s.id
                    ? "border-blue-600 bg-blue-50 text-blue-700 font-medium"
                    : "border-gray-200 text-gray-600 hover:bg-gray-50",
                ].join(" ")}
              >
                {s.rotulo}
              </button>
            ))}
          </div>
        </div>

        <CampoPeriodo
          inicio={filtros.inicio}
          fim={filtros.fim}
          aoMudar={({ inicio, fim }) => setFiltros((f) => ({ ...f, inicio, fim }))}
        />

        {isFetching && (
          <span className="text-sm text-gray-500">Atualizando…</span>
        )}
      </div>

      {buscando && data && !isLoading && (
        <p className="text-sm text-gray-500" aria-live="polite">
          {filtrados.length === 1
            ? "1 chamado desta aba corresponde à busca"
            : `${filtrados.length} chamados desta aba correspondem à busca`}
          {itens.length > 0 && ` (de ${itens.length})`}.
        </p>
      )}

      {/* Aviso das duas bases de contagem */}
      {temPeriodo && gerais && contadores && (
        <p className="text-sm text-gray-500 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
          <span>
            As abas contam {contadores.todos} chamado(s) dentro do período e do
            tipo escolhidos. O total do cliente, sem esses filtros, é{" "}
            {gerais.todos}.
          </span>
        </p>
      )}

      {isLoading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      )}

      {isError && (
        <ErrorAlert
          title="Não foi possível carregar os chamados"
          message={(error as Error)?.message ?? "Tente novamente."}
          onRetry={() => refetch()}
          variant="error"
        />
      )}

      {data && !isLoading && !isError && (
        // Enquanto a lista nova não chega, a anterior fica no lugar, apagada.
        <div
          className={
            isPlaceholderData ? "opacity-50 transition-opacity" : "transition-opacity"
          }
        >
          {buscando && filtrados.length === 0 ? (
            <SemResultado busca={busca} aoLimpar={() => setBusca("")} />
          ) : filtros.aba === "Novas" ? (
            <QuadroSeveridades
              itens={filtrados}
              todos={novasCompleta.data?.itens}
              podeReordenar={podeReordenar && novasCompleta.isSuccess}
            />
          ) : (
            <TabelaChamados itens={filtrados} />
          )}
        </div>
      )}
    </div>
  );
};

const SemResultado = ({ busca, aoLimpar }: { busca: string; aoLimpar: () => void }) => (
  <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-12 text-center">
    <p className="text-gray-600 font-medium">
      Nenhum chamado desta aba corresponde a “{busca.trim()}”.
    </p>
    <p className="text-gray-500 text-sm mt-1">
      A busca olha número, assunto, descrição, produto e tipo. Outra aba ou outro tipo de demanda
      podem ter o que você procura.
    </p>
    <button
      type="button"
      onClick={aoLimpar}
      className="mt-4 text-sm font-medium text-blue-600 transition-colors hover:text-blue-500"
    >
      Limpar busca
    </button>
  </div>
);

const TabelaChamados = ({ itens }: { itens: ChamadoGestao[] }) => {
  if (itens.length === 0) {
    return (
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-12 text-center">
        <p className="text-gray-600 font-medium">
          Nenhum chamado nesta combinação de filtros.
        </p>
        <p className="text-gray-500 text-sm mt-1">
          Ajuste a situação, o tipo de demanda ou o período.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50/80 text-gray-600">
            <tr>
              <th className="text-left font-semibold px-4 py-3">Chamado</th>
              <th className="text-left font-semibold px-4 py-3">Severidade</th>
              <th className="text-left font-semibold px-4 py-3">Situação</th>
              <th className="text-left font-semibold px-4 py-3">Produto</th>
              <th className="text-left font-semibold px-4 py-3">Aberto em</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {itens.map((c) => (
              <tr key={c.id} className="hover:bg-blue-50/40 transition-colors">
                <td className="px-4 py-3">
                  <Link
                    to={`/chamados/${c.id}`}
                    className="font-medium text-blue-700 hover:text-blue-900"
                  >
                    #{c.id} · {c.assunto}
                  </Link>
                  {descricaoUtil(c) && (
                    <p className="mt-0.5 max-w-xl truncate text-sm text-gray-600">
                      {descricaoUtil(c)}
                    </p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                    {c.categoriaChamado && <span>{c.categoriaChamado}</span>}
                    {c.quantidadeInteracoes > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" />
                        {c.quantidadeInteracoes}
                      </span>
                    )}
                    {c.possuiAnexo && (
                      <span className="inline-flex items-center gap-1">
                        <Paperclip className="w-3 h-3" />
                        anexo
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={[
                      "inline-block rounded-full border px-2.5 py-0.5 text-xs font-semibold",
                      classesSeveridade(c.severidade),
                    ].join(" ")}
                  >
                    {rotuloSeveridade(c.severidade)}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-700">
                  {c.statusChamado ?? "—"}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {c.projetoNome ?? "—"}
                </td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                  {formatarData(c.dataCriacao)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ChamadosPage;
